import {
  directUpload,
  FileDropzone,
  Upload as UploadResult,
} from '@execonline-inc/file-upload.private';
import { identity } from '@kofno/piper';
import { observer } from 'mobx-react';
import * as React from 'react';
import WithCurrentUser from '../../CurrentUser/Context/WithCurrentUser';
import { CurrentUserResource } from '../../CurrentUser/Types';
import FileUploadStore from '../../FileUploadStore';
import { T, translation, TranslationsContext } from '../../Translations';
import {
  DirectUploadFailure,
  directUploadConfig,
  photoErrorMessage,
  profilePhotoRules,
} from '../../Uploads/DirectUploadConfig';
import Button from '../Form/Button';
import * as style from './style.module.css';

interface Props {
  store: FileUploadStore;
  buttonClass: string;
}

/**
 * Profile photo picker. Drag a file onto the area or use the button, then the
 * browser uploads it straight to S3 and the store keeps the resulting signed
 * id for the profile form to submit.
 */
const FileUpload: React.FC<Props> = ({ store, buttonClass }) => (
  <WithCurrentUser
    children={(currentUserResource) => (
      <TranslationsContext.Consumer>
        {(ts) => (
          <FileDropzone
            rules={profilePhotoRules}
            className={style.dropzone}
            activeClassName={style.dropzoneActive}
            inputLabel={translation('Photo')(ts)}
            onAccepted={onAccepted(store, currentUserResource)}
            onRejected={(error) => store.error(photoErrorMessage(error))}
            data-intercom-target="profile-photo-dropzone"
          >
            {(open) => (
              <div data-test-uploading-available="add a photo">
                <span className={style.dropHint}>
                  <T kind="Adding a photo enhances your ExecOnline experience ..." />
                </span>
                <Button
                  type="button"
                  value="add a photo"
                  onClick={open}
                  className={buttonClass}
                  intercomTarget="profile-photo-add-button"
                />
              </div>
            )}
          </FileDropzone>
        )}
      </TranslationsContext.Consumer>
    )}
  />
);

// The rules cap this picker at one file, so only the first is ever present.
const onAccepted =
  (store: FileUploadStore, currentUserResource: CurrentUserResource) =>
  (files: ReadonlyArray<File>): void => {
    files.slice(0, 1).forEach((file) => {
      store.uploadInProgess();

      directUploadConfig(currentUserResource, 'profile_avatar')
        .andThen((config) => directUpload(file, config).mapError<DirectUploadFailure>(identity))
        .fork(
          (error) => store.error(photoErrorMessage(error)),
          (upload: UploadResult) => {
            if (upload.kind === 'blob-upload') {
              store.uploadSuccessful({
                signedId: upload.signedId,
                filename: upload.filename,
                previewUrl: URL.createObjectURL(file),
              });
            }
          },
        );
    });
  };

export default observer(FileUpload);
